import { Link, useSearchParams } from 'react-router-dom';
import { useFeishuEvents } from '@/api/hooks';
import { ErrorCard } from '@/components/ErrorCard';
import { SkeletonRow } from '@/components/SkeletonRow';
import { StatusBadge } from '@/components/StatusBadge';
import { formatTimestamp } from '@/utils/format';
import { PageHeader, Panel, Select, Td, TextInput, Th } from './pageParts';

const MAX_PAGE_LIMIT = 200;

function pageLimitParam(searchParams: URLSearchParams, key: string, fallback: number): number {
  const parsed = Number(searchParams.get(key));
  const value = Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  return Math.min(MAX_PAGE_LIMIT, Math.max(1, Math.floor(value)));
}

function nonNegativeNumberParam(searchParams: URLSearchParams, key: string): number {
  const parsed = Number(searchParams.get(key));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export function FeishuEventsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get('status') || 'all';
  const eventType = searchParams.get('event_type') || '';
  const since = searchParams.get('since') || '';
  const limit = pageLimitParam(searchParams, 'limit', 50);
  const offset = nonNegativeNumberParam(searchParams, 'offset');
  const events = useFeishuEvents({ status, event_type: eventType, since, limit, offset });
  const returnedCount = events.data?.events.length ?? 0;
  const totalCount = events.data?.total_count ?? 0;
  const canPrevious = offset > 0;
  const canNext = offset + returnedCount < totalCount;

  function updateUrl(updates: Record<string, string | number | null>, resetOffset = false) {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(updates)) {
      if (value === null || value === '' || (key === 'status' && value === 'all')) next.delete(key);
      else next.set(key, String(value));
    }
    if (resetOffset) next.delete('offset');
    setSearchParams(next, { replace: true });
  }

  return (
    <div className="flex flex-col gap-panel-gap">
      <PageHeader title="Feishu Events" subtitle="飞书事件日志列表；列表响应不包含 raw_payload，详情页按需查看敏感 payload。" meta={events.data ? `observed_at ${events.data.observed_at} · ${formatTimestamp(events.data.observed_at)}` : 'server observed_at'} />
      <Panel>
        <div className="flex flex-wrap items-center gap-stack-md">
          <label className="font-label-caps text-label-caps uppercase text-on-surface-variant">Status</label>
          <Select aria-label="Status" value={status} onChange={(e) => updateUrl({ status: e.target.value }, true)}>
            <option value="all">all</option><option value="pending">pending</option><option value="processed">processed</option><option value="failed">failed</option><option value="logged_only">logged_only</option>
          </Select>
          <TextInput aria-label="event_type" value={eventType} onChange={(e) => updateUrl({ event_type: e.target.value }, true)} placeholder="event_type exact" />
          <TextInput aria-label="since" value={since} onChange={(e) => updateUrl({ since: e.target.value }, true)} placeholder="since RFC3339" />
          <Select aria-label="limit" value={limit} onChange={(e) => updateUrl({ limit: Number(e.target.value) }, true)}>
            <option value={50}>50</option><option value={100}>100</option><option value={200}>200</option>
          </Select>
        </div>
      </Panel>
      {events.isPending ? <Panel><SkeletonRow count={8} cells={7} /></Panel> : events.error ? <ErrorCard title="Feishu events 请求失败" status={events.error.status} body={events.error.body} /> : (
        <Panel title={`Events · ${returnedCount === 0 ? 0 : offset + 1}-${offset + returnedCount} / total ${totalCount}`}>
          <div className="overflow-x-auto"><table className="w-full border-collapse text-left"><thead><tr className="border-b border-outline-variant bg-[#EBECF0] font-label-caps text-label-caps uppercase"><Th>received_at</Th><Th>event_id</Th><Th>event_type</Th><Th>status</Th><Th>tenant</Th><Th>attempts</Th><Th>error</Th></tr></thead><tbody className="divide-y divide-outline-variant font-body-sm text-body-sm">
            {(events.data?.events ?? []).map((event) => <tr key={event.event_id} className="hover:bg-surface-container-low"><Td className="font-code-md text-code-md">{formatTimestamp(event.received_at)}</Td><Td><Link className="font-code-md text-code-md text-primary underline" to={`/ops/feishu-events/${event.event_id}`}>{event.event_id}</Link></Td><Td className="font-code-md text-code-md">{event.event_type}</Td><Td><StatusBadge variant={event.dispatch_status === 'failed' ? 'danger' : event.dispatch_status === 'processed' ? 'success' : 'neutral'} dot>{event.dispatch_status}</StatusBadge></Td><Td>{event.tenant_key ?? '-'}</Td><Td>{event.attempt_count}</Td><Td>{event.process_error ?? '-'}</Td></tr>)}
          </tbody></table></div>
          <div className="mt-stack-md flex items-center justify-between gap-stack-md font-body-sm text-body-sm text-on-surface-variant">
            <span>offset={offset} · limit={limit}</span>
            <div className="flex gap-stack-sm">
              <button type="button" disabled={!canPrevious} onClick={() => updateUrl({ offset: Math.max(0, offset - limit) })} className="rounded border border-outline-variant px-stack-md py-stack-sm disabled:opacity-50">Previous</button>
              <button type="button" disabled={!canNext} onClick={() => updateUrl({ offset: offset + limit })} className="rounded border border-outline-variant px-stack-md py-stack-sm disabled:opacity-50">Next</button>
            </div>
          </div>
        </Panel>
      )}
    </div>
  );
}
